import type { FormEvent, KeyboardEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import LineEditIcon from 'LuxKit/Icons/line/LineEditIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LineArrowToBottomIcon from 'LuxKit/Icons/line/LineArrowToBottomIcon'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import TripNameField from 'tripPlanner/assistant/components/TripNameField'

const ActionList = styled.div`
  display: flex;
  flex-direction: column;
  padding-block-end: env(safe-area-inset-bottom);
`

const ActionListItem = styled(ClickableListItem)`
  min-height: ${rem(64)};
  border-radius: 0;
  border-block-start: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &[aria-disabled="true"] {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    pointer-events: none;
  }
`

const ActionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
  color: currentColor;
`

const RenameFields = styled.div`
  display: grid;
  gap: ${rem(16)};
  padding-block-end: ${rem(16)};
`

interface Props {
  title: string
  onClose: () => void
  onRename: (name: string) => Promise<void>
  onDelete: () => void
  onDownloadPdf?: () => void
  onDownloadSpreadsheet?: () => void
}

function RecentChatItemActionsSheet({
  title,
  onClose,
  onRename,
  onDelete,
  onDownloadPdf,
  onDownloadSpreadsheet,
}: Props) {
  const intl = useIntl()
  const renameInFlightRef = useRef(false)
  const [displayTitle, setDisplayTitle] = useState(title)
  const [isRenameFormOpen, setIsRenameFormOpen] = useState(false)
  const [draftName, setDraftName] = useState(title)
  const [renameError, setRenameError] = useState<string | undefined>(undefined)
  const [isRenameLoading, setIsRenameLoading] = useState(false)

  useEffect(() => {
    setDisplayTitle(title)
    if (!isRenameFormOpen) {
      setDraftName(title)
    }
  }, [isRenameFormOpen, title])

  const renameActionLabel = intl.formatMessage({
    id: 'GxgzBhxzJ9b2o4pp_qh-',
    defaultMessage: 'Rename chat',
    description: 'Action sheet option label to rename an AI chat from the TripPlanner recent chats list on mobile',
  })
  const renameInputLabel = intl.formatMessage({
    id: 'twg62LlCQW0XcUwXU7ac',
    defaultMessage: 'Chat name',
    description: 'Input label for renaming an AI chat from the TripPlanner recent chats mobile action sheet',
  })
  const deleteLabel = intl.formatMessage({
    id: 'Ayi68CFOR9I7O19zqL4o',
    defaultMessage: 'Delete',
    description: 'Action sheet option label to delete a chat or trip from the TripPlanner recent chats list on mobile',
  })
  const downloadPdfLabel = intl.formatMessage({
    id: 'jo_-jnJZtobDXF7aDm0t',
    defaultMessage: 'Download PDF',
    description: 'Action menu option that downloads the selected TripPlanner chat itinerary as a PDF file',
  })
  const downloadSpreadsheetLabel = intl.formatMessage({
    id: 'y3sx-ml3qCx4k6P1fjtD',
    defaultMessage: 'Download spreadsheet',
    description: 'Action menu option that downloads the selected TripPlanner chat itinerary as a spreadsheet file',
  })
  const renameFailedError = intl.formatMessage({
    id: 'u9W4OX7Vp7suSkRx8Jy-',
    defaultMessage: 'Unable to rename. Please try again.',
    description: 'Snackbar error shown when renaming a chat or trip from the TripPlanner recent chats mobile sheet fails',
  })
  const emptyNameError = intl.formatMessage({
    id: 'fa7RG3pZcl3DF-hgzDUz',
    defaultMessage: 'Name cannot be empty',
    description: 'Validation error shown when the TripPlanner recent chats mobile rename input is left empty',
  })
  const saveLabel = intl.formatMessage({
    id: 'FU0fYHaAb-ca0JGBqsGY',
    defaultMessage: 'Save',
    description: 'Button label to save a renamed chat or trip from the TripPlanner recent chats mobile action sheet',
  })
  const cancelLabel = intl.formatMessage({
    id: 'F97Bq_B3QWiVKPqpkTHI',
    defaultMessage: 'Cancel',
    description: 'Button label to cancel renaming a chat or trip from the TripPlanner recent chats mobile action sheet',
  })

  const openRenameForm = useCallback(() => {
    setDraftName(displayTitle)
    setRenameError(undefined)
    setIsRenameFormOpen(true)
  }, [displayTitle])

  const closeRenameForm = useCallback(() => {
    setDraftName(displayTitle)
    setRenameError(undefined)
    setIsRenameFormOpen(false)
  }, [displayTitle])

  const handleRenameChange = useCallback((nextName: string) => {
    setDraftName(nextName)
    setRenameError(undefined)
  }, [])

  const canDismiss = !isRenameLoading

  const handleRenameSubmit = useCallback(async() => {
    if (isRenameLoading || renameInFlightRef.current) return

    const trimmedName = draftName.trim()
    if (!trimmedName) {
      setRenameError(emptyNameError)
      return
    }

    if (trimmedName === displayTitle.trim()) {
      closeRenameForm()
      return
    }

    renameInFlightRef.current = true
    setIsRenameLoading(true)
    try {
      await onRename(trimmedName)
      setDisplayTitle(trimmedName)
      onClose()
    } catch(error) {
      console.error('[TripChat] Failed to rename recent chat item', error)
      setRenameError(renameFailedError)
      showSnackbar(renameFailedError, 'critical')
    } finally {
      renameInFlightRef.current = false
      setIsRenameLoading(false)
    }
  }, [
    closeRenameForm,
    displayTitle,
    draftName,
    emptyNameError,
    isRenameLoading,
    onClose,
    onRename,
    renameFailedError,
  ])

  const handleRenameSaveClick = useCallback<NonNullable<ModalFooterActionProps['onClick']>>(async(event) => {
    event.preventDefault()
    await handleRenameSubmit()
  }, [handleRenameSubmit])

  const handleRenameFormSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleRenameSubmit()
  }, [handleRenameSubmit])

  const handleRenameFormKeyDown = useCallback((event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Escape' && !isRenameLoading) {
      event.stopPropagation()
      event.preventDefault()
      closeRenameForm()
    }
  }, [closeRenameForm, isRenameLoading])

  const handleDeleteClick = useCallback(() => {
    if (isRenameLoading) return
    onDelete()
  }, [isRenameLoading, onDelete])

  const handleDownloadPdfClick = useCallback(() => {
    onDownloadPdf?.()
    onClose()
  }, [onClose, onDownloadPdf])

  const handleDownloadSpreadsheetClick = useCallback(() => {
    onDownloadSpreadsheet?.()
    onClose()
  }, [onClose, onDownloadSpreadsheet])

  const isRenameSaveDisabled = isRenameLoading || !draftName.trim()

  const renamePrimaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    children: saveLabel,
    disabled: isRenameSaveDisabled,
    onClick: handleRenameSaveClick,
    type: 'submit',
  }), [handleRenameSaveClick, isRenameSaveDisabled, saveLabel])

  const renameSecondaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    children: cancelLabel,
    disabled: isRenameLoading,
    onClick: closeRenameForm,
    type: 'button',
  }), [cancelLabel, closeRenameForm, isRenameLoading])

  const renameActionIcon = useMemo(() => (
    <ActionIcon aria-hidden="true">
      <LineEditIcon />
    </ActionIcon>
  ), [])

  const deleteActionIcon = useMemo(() => (
    <ActionIcon aria-hidden="true">
      <LineTrashAltIcon />
    </ActionIcon>
  ), [])

  const downloadActionIcon = useMemo(() => (
    <ActionIcon aria-hidden="true">
      <LineArrowToBottomIcon />
    </ActionIcon>
  ), [])

  if (isRenameFormOpen) {
    return (
      <ModalBase dismissible={canDismiss} height="auto" size="S">
        <form onSubmit={handleRenameFormSubmit} onKeyDown={handleRenameFormKeyDown}>
          <ModalHeader
            dismissible={canDismiss}
            title={renameActionLabel}
            onBackButtonClick={canDismiss ? closeRenameForm : undefined}
            onCloseButtonClick={canDismiss ? onClose : undefined}
          />
          <ModalBody>
            <ModalContent>
              <RenameFields>
                <TripNameField
                  label={renameInputLabel}
                  value={draftName}
                  onChange={handleRenameChange}
                  autoSelect
                  disabled={isRenameLoading}
                />
                {renameError && <MessageBanner kind="critical" density="compact">
                  {renameError}
                </MessageBanner>}
              </RenameFields>
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={renamePrimaryActionProps}
            secondaryActionProps={renameSecondaryActionProps}
          />
        </form>
      </ModalBase>
    )
  }

  return (
    <ModalBase dismissible={!isRenameLoading} height="auto" size="S">
      <ModalHeader
        title={displayTitle}
        onCloseButtonClick={onClose}
      />
      <ModalBody>
        <ModalContent flushListItems>
          <ActionList>
            {onDownloadPdf && (
              <ActionListItem
                type="button"
                aria-disabled={isRenameLoading}
                disabled={isRenameLoading}
                onClick={handleDownloadPdfClick}
                size="large"
                tabIndex={isRenameLoading ? -1 : undefined}
                title={downloadPdfLabel}
                startIcon={downloadActionIcon}
              />
            )}
            {onDownloadSpreadsheet && (
              <ActionListItem
                type="button"
                aria-disabled={isRenameLoading}
                disabled={isRenameLoading}
                onClick={handleDownloadSpreadsheetClick}
                size="large"
                tabIndex={isRenameLoading ? -1 : undefined}
                title={downloadSpreadsheetLabel}
                startIcon={downloadActionIcon}
              />
            )}
            <ActionListItem
              type="button"
              aria-disabled={isRenameLoading}
              disabled={isRenameLoading}
              onClick={openRenameForm}
              size="large"
              tabIndex={isRenameLoading ? -1 : undefined}
              title={renameActionLabel}
              startIcon={renameActionIcon}
            />
            <ActionListItem
              type="button"
              aria-disabled={isRenameLoading}
              disabled={isRenameLoading}
              onClick={handleDeleteClick}
              size="large"
              tabIndex={isRenameLoading ? -1 : undefined}
              title={deleteLabel}
              startIcon={deleteActionIcon}
            />
          </ActionList>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default RecentChatItemActionsSheet
